import { sortTaskStatuses } from '../../../utils/statusUtils';

/**
 * Single multiselect value: matches Task.status `CLOSED` or `FINALIZED` (not included in default filter).
 */
export const SCHEDULE_PAGE_STATUS_CLOSED_FINALIZED = 'CLOSED_OR_FINALIZED' as const;

/** Task workflow stages in the Status filter (one grouped item for closed/finalized). */
export const SCHEDULE_PAGE_TASK_STATUSES = [
  'PROPOSED',
  'PLANNED',
  'ACTIVE',
  'ACTIVE_MARKED_AS_COMPLETE',
  'INTERNAL_REVIEW',
  SCHEDULE_PAGE_STATUS_CLOSED_FINALIZED,
] as const;

/**
 * Status options as the Schedule filter presents them: lifecycle order (`TASK_STATUS_SEQUENCE`),
 * not declaration order. The grouped Closed/Finalized entry ranks where CLOSED sits.
 *
 * CANCELLED is deliberately absent — the schedule endpoints only ever return the stages in
 * `SCHEDULE_TASK_STAGES`, so offering it would guarantee an empty chart.
 */
export const SCHEDULE_PAGE_STATUS_FILTER_OPTIONS: string[] = sortTaskStatuses(
  [...SCHEDULE_PAGE_TASK_STATUSES],
  (status) => (status === SCHEDULE_PAGE_STATUS_CLOSED_FINALIZED ? 'CLOSED' : status)
);

/** Initial Status multiselect on Schedule page (excludes Proposed, Internal Review, and Closed/Finalized). */
export const SCHEDULE_PAGE_DEFAULT_STATUS_FILTER: readonly string[] = [
  'PLANNED',
  'ACTIVE',
  'ACTIVE_MARKED_AS_COMPLETE',
];

export type SchedulePageTaskStatus = (typeof SCHEDULE_PAGE_TASK_STATUSES)[number];

/** Title-case labels for the multiselect (values remain enum strings). */
export function formatScheduleTaskStatusLabel(status: string): string {
  const s = String(status || '').trim();
  if (s === SCHEDULE_PAGE_STATUS_CLOSED_FINALIZED) return 'Closed / Finalized';
  return s
    .split(/_+/)
    .filter(Boolean)
    .map((w) => w.charAt(0) + w.slice(1).toLowerCase())
    .join(' ');
}

/** True if `taskStatus` is allowed by the schedule status multiselect (expands the closed/finalized group). */
export function taskStatusMatchesScheduleSelection(taskStatus: string, selectedStatuses: string[]): boolean {
  const st = String(taskStatus ?? '').trim();
  const sel = selectedStatuses.map((x) => String(x || '').trim()).filter((x) => x.length > 0);
  if (sel.length === 0) return true;
  if (sel.includes(st)) return true;
  if (
    sel.includes(SCHEDULE_PAGE_STATUS_CLOSED_FINALIZED) &&
    (st === 'CLOSED' || st === 'FINALIZED')
  ) {
    return true;
  }
  return false;
}
